import React, { useMemo } from "react";
import { calculateColor, capitalizeFirstLetter } from "./stockUtils";

const Value = ({ title, style, children }) => (
    <span
        className="value-tooltip"
        data-tooltip={title || ""}
        style={style}
        tabIndex={0}
    >
        {children}
    </span>
);

function StockDetails({ stock, sectionClass, highlightStyle }) {
    const interactionHint = useMemo(() => {
        if (typeof window === "undefined") {
            return "Hover over values for explanations!";
        }

        const hasCoarsePointer =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(pointer: coarse)").matches;
        const hasTouch =
            ("ontouchstart" in window ||
                (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0) ||
                (typeof navigator !== "undefined" && navigator.msMaxTouchPoints > 0));

        return hasCoarsePointer || hasTouch
            ? "Tap values for explanations!"
            : "Hover over values for explanations!";
    }, []);
    const trailingPEColor = calculateColor(stock.trailingPE, "trailingPE");
    const threeYearPEColor = calculateColor(stock.threeYearPE, "threeYearPE");
    const longTermDebtColor = calculateColor(stock.debtToEquity, "longTermDebt");
    const equityColor = calculateColor(stock.debtToEquity, "equity");
    const debtToEquityColor = calculateColor(stock.debtToEquity, "debtToEquity");
    const earningsVelocityColor = calculateColor(
        stock.earningsVelocity,
        "earningsVelocity"
    );
    const priceToAssetsColor = calculateColor(stock.priceToAssets, "priceToAssets");
    const interestCoverageColor = calculateColor(
        stock.interestCoverageTTM,
        "interestCoverage"
    );
    const fcfMarginColor = calculateColor(stock.fcfMarginTTM, "fcfMargin");
    const grossMarginColor = calculateColor(stock.grossMarginTTM, "grossMargin");
    const operatingMarginColor = calculateColor(
        stock.operatingMarginTTM_Q,
        "operatingMargin"
    );
    const netMarginColor = calculateColor(stock.netMarginTTM, "netMargin");
    const fcfYoYGrowthColor = calculateColor(stock.fcfYoYGrowth, "fcfYoYGrowth");
    const revenueYoYGrowthColor = calculateColor(
        stock.revenueYoYGrowthTTM,
        "revenueYoYGrowth"
    );
    const netIncomeYoYGrowthColor = calculateColor(
        stock.netIncomeYoYGrowthTTM,
        "netIncomeYoYGrowth"
    );
    const fcfToDebtColor = calculateColor(stock.fcfToDebt, "fcfToDebt");
    const fcfToNetIncomeColor = calculateColor(
        stock.fcfToNetIncome,
        "fcfToNetIncome"
    );

    return (
        <>
            {/* BASIC INFO */}
            <div className={sectionClass} style={highlightStyle}>
                <p className="centeredp">
                    Name:{" "}
                    <Value title="Official company name.">
                        {stock.name}
                    </Value>
                </p>
                <p>
                    Industry:{" "}
                    <Value title="Primary industry classification.">
                        {capitalizeFirstLetter(stock.industry)}
                    </Value>
                </p>
                <p>
                    Ticker:{" "}
                    <Value title="Exchange ticker symbol used for trading.">
                        {stock.ticker}
                    </Value>
                </p>
                <p>
                    Price: $
                    <Value title="Latest share price.">
                        {stock.currentPrice}
                    </Value>
                </p>
            </div>

            {/* CORE FUNDAMENTALS */}
            <div className={sectionClass}>
                <p className="centeredp">Company Details</p>
                <p className="interaction-hint">{interactionHint}</p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Trailing PE:{" "}
                    <Value
                        title="Price divided by trailing 12-month earnings per share."
                        style={{ color: trailingPEColor }}
                    >
                        {stock.trailingPE}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Three Year PE:{" "}
                    <Value
                        title="Average price/earnings ratio over the last three years."
                        style={{ color: threeYearPEColor }}
                    >
                        {stock.threeYearPE}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Long Term Debt:{" "}
                    <Value
                        title="Total long-term debt on the balance sheet."
                        style={{ color: longTermDebtColor }}
                    >
                        ${stock.longTermDebt.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Equity:{" "}
                    <Value
                        title="Shareholders' equity (assets minus liabilities)."
                        style={{ color: equityColor }}
                    >
                        ${stock.equity.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Debt/Equity:{" "}
                    <Value
                        title="Debt-to-equity ratio; leverage level."
                        style={{ color: debtToEquityColor }}
                    >
                        {stock.debtToEquity}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Earnings Velocity:{" "}
                    <Value
                        title="Recent EPS momentum; higher is faster growth."
                        style={{ color: earningsVelocityColor }}
                    >
                        {stock.earningsVelocity}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Total Assets: $
                    <Value title="Total assets on the balance sheet.">
                        {stock.totalAssets.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Tangible Assets: $
                    <Value title="Assets excluding goodwill and other intangibles.">
                        {stock.tangibleAssets.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Price/Assets:{" "}
                    <Value
                        title="Market value relative to tangible assets."
                        style={{ color: priceToAssetsColor }}
                    >
                        {stock.priceToAssets}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Dividend Yield:{" "}
                    <Value title="Annual dividends as a percent of share price.">
                        {stock.dividendYield}%
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Total Cash: $
                    <Value title="Cash and cash equivalents on hand.">
                        {stock.totalCash.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Total Cash/Share: $
                    <Value title="Cash per outstanding share.">
                        {stock.totalCashPerShare}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Shares Outstanding:{" "}
                    <Value title="Number of shares currently outstanding.">
                        {stock.sharesOutstanding.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    Description:{" "}
                    <Value title="Brief company description.">
                        {stock.description}
                    </Value>
                </p>
            </div>

            {/* CASH FLOW & PROFITABILITY */}
            <div className={sectionClass}>
                <p className="centeredp">Cash Flow & Profitability</p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Revenue (TTM): $
                    <Value title="Revenue generated over the last twelve months.">
                        {stock.revenueTTM.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Gross Profit (TTM): $
                    <Value title="Gross profit over the last twelve months.">
                        {stock.grossProfitTTM.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;EBIT (TTM): $
                    <Value title="Earnings before interest and taxes over the last twelve months.">
                        {stock.ebitTTM.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Net Income (TTM): $
                    <Value title="Net income over the last twelve months.">
                        {stock.netIncomeTTM.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Gross Margin (TTM):{" "}
                    <Value
                        title="Gross profit as a percent of revenue (TTM)."
                        style={{ color: grossMarginColor }}
                    >
                        {stock.grossMarginTTM}%
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Operating Margin (TTM):{" "}
                    <Value
                        title="Operating margin as a percent of revenue (TTM)."
                        style={{ color: operatingMarginColor }}
                    >
                        {stock.operatingMarginTTM_Q}%
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Net Margin (TTM):{" "}
                    <Value
                        title="Net income as a percent of revenue (TTM)."
                        style={{ color: netMarginColor }}
                    >
                        {stock.netMarginTTM}%
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Interest Coverage (TTM):{" "}
                    <Value
                        title="EBIT divided by interest expense; ability to cover interest."
                        style={{ color: interestCoverageColor }}
                    >
                        {stock.interestCoverageTTM}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Free Cash Flow (TTM): $
                    <Value title="Free cash flow generated over the last twelve months.">
                        {stock.fcfTTM.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;FCF Margin (TTM):{" "}
                    <Value
                        title="Free cash flow as a percent of revenue (TTM)."
                        style={{ color: fcfMarginColor }}
                    >
                        {stock.fcfMarginTTM}%
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;FCF/Debt:{" "}
                    <Value
                        title="Free cash flow divided by total debt."
                        style={{ color: fcfToDebtColor }}
                    >
                        {stock.fcfToDebt}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;FCF/Net Income:{" "}
                    <Value
                        title="Free cash flow divided by net income."
                        style={{ color: fcfToNetIncomeColor }}
                    >
                        {stock.fcfToNetIncome}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Revenue Trend (per quarter): $
                    <Value title="Average revenue change per quarter.">
                        {stock.revenueTrendQ.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Net Income Trend (per quarter): $
                    <Value title="Average net income change per quarter.">
                        {stock.netIncomeTrendQ.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Operating Margin Trend (per quarter):{" "}
                    <Value title="Change in operating margin each quarter.">
                        {stock.operatingMarginTrendQ}%
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;FCF Trend (per quarter): $
                    <Value title="Change in free cash flow each quarter.">
                        {stock.fcfTrend.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Operating Cash Flow Trend (per quarter): $
                    <Value title="Change in operating cash flow each quarter.">
                        {stock.operatingCashflowTrend.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Capex Trend (per quarter): $
                    <Value title="Change in capital expenditures each quarter.">
                        {stock.capexTrend.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;FCF YoY Growth (TTM):{" "}
                    <Value
                        title="Year-over-year growth in free cash flow (TTM)."
                        style={{ color: fcfYoYGrowthColor }}
                    >
                        {stock.fcfYoYGrowth}%
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Revenue YoY Growth (TTM):{" "}
                    <Value
                        title="Year-over-year revenue growth (TTM)."
                        style={{ color: revenueYoYGrowthColor }}
                    >
                        {stock.revenueYoYGrowthTTM}%
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Net Income YoY Growth (TTM):{" "}
                    <Value
                        title="Year-over-year net income growth (TTM)."
                        style={{ color: netIncomeYoYGrowthColor }}
                    >
                        {stock.netIncomeYoYGrowthTTM}%
                    </Value>
                </p>
            </div>

            {/* SENTIMENT & INSIDERS */}
            <div className={sectionClass}>
                <p className="centeredp">Sentiment & Insiders</p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;News Sentiment Range:{" "}
                    <Value title="Spread between the most positive and negative news scores.">
                        {stock.sentiment_range}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;News Sentiment Average:{" "}
                    <Value title="Average news sentiment score across articles.">
                        {stock.sentiment_avg}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;News Sentiment Median:{" "}
                    <Value title="Median news sentiment score.">
                        {stock.sentiment_median}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Insider Net Shares (1 year):{" "}
                    <Value title="Net shares insiders bought minus sold over the last year.">
                        {stock.insider_net_shares_1y.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Insider Buys (1 year):{" "}
                    <Value title="Count of insider purchase transactions in the last year.">
                        {stock.insider_buys_1y}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Insider Sells (1 year):{" "}
                    <Value title="Count of insider sale transactions in the last year.">
                        {stock.insider_sells_1y}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Insider Buy Volume (1 year): $
                    <Value title="Dollar value of insider purchases in the last year.">
                        {stock.insider_buy_dollar_1y.toLocaleString("en-US")}
                    </Value>
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;Insider Sell Volume (1 year): $
                    <Value title="Dollar value of insider sales in the last year.">
                        {stock.insider_sell_dollar_1y.toLocaleString("en-US")}
                    </Value>
                </p>
            </div>
        </>
    );
}

export default StockDetails;
